import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from './client';

export type Resource<T> = {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
};

/**
 * Lecture d'une ressource.
 *
 * `path` à `null` court-circuite la requête : c'est ce qui permet d'attendre qu'un
 * identifiant soit connu sans multiplier les branches dans les composants.
 */
export function useResource<T>(path: string | null): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [tick, setTick] = useState(0);
  // Évite d'appliquer la réponse d'une requête devenue obsolète.
  const generation = useRef(0);

  useEffect(() => {
    if (path === null) {
      setData(null);
      setLoading(false);
      return;
    }
    const current = ++generation.current;
    setLoading(true);
    setError(null);

    api
      .get<T>(path)
      .then((result) => {
        if (generation.current !== current) return;
        setData(result);
      })
      .catch((cause: unknown) => {
        if (generation.current !== current) return;
        setError(cause instanceof ApiError ? cause : new ApiError(0, 'network'));
      })
      .finally(() => {
        if (generation.current === current) setLoading(false);
      });
  }, [path, tick]);

  const reload = useCallback(() => setTick((value) => value + 1), []);
  return { data, error, loading, reload };
}

export type Mutation<TInput, TResult> = {
  run: (input: TInput) => Promise<TResult | null>;
  pending: boolean;
  error: ApiError | null;
  reset: () => void;
};

/** Mutation avec état de chargement et dernière erreur, pour l'affichage en ligne. */
export function useMutation<TInput, TResult>(
  action: (input: TInput) => Promise<TResult>,
): Mutation<TInput, TResult> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const run = useCallback(
    async (input: TInput): Promise<TResult | null> => {
      setPending(true);
      setError(null);
      try {
        return await action(input);
      } catch (cause) {
        setError(cause instanceof ApiError ? cause : new ApiError(0, 'network'));
        return null;
      } finally {
        setPending(false);
      }
    },
    [action],
  );

  return { run, pending, error, reset: () => setError(null) };
}
